import { Injectable, OnDestroy, Renderer2 } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LocalStorageService } from "ngx-webstorage";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { TranslationLoaderService } from "../../internals/translation-loader.service";
import { AuthService } from "../auth.service";
import { SingletonCheckHelper } from '../../helpers/singleton-check.helper';
import { TcmCultureSystemService } from '../../internals/tcm-culture-system-service';


@Injectable()
export class SetupTranslationSystemService implements OnDestroy {
    private isInitialized = false;
    private translationModeKey = "isTranslationMode";
    public translationMode$ = new BehaviorSubject<boolean>(false);

    private userSubscription: Subscription;

    private readonly TranslationIdKey: string = 'TranslationId';

    constructor(
        private readonly authService: AuthService,
        private readonly localStorageService: LocalStorageService,
        private readonly translateService: TranslateService,
        private readonly translationLoaderService: TranslationLoaderService,
        private readonly tcmCultureSystemService: TcmCultureSystemService) {
        SingletonCheckHelper.ensureSingleton(this);
    }

    ngOnDestroy(): void {
        this.userSubscription.unsubscribe();
    }

    async initAsync(): Promise<any> {
        if (this.isInitialized)
            return;

        let translationId, culture;

        if(this.authService.currentUserValue) {
            translationId = this.authService.currentUserValue.translationId;
            culture = this.authService.currentUserValue.culture;
            this.setTranslationModeValue(this.authService.currentUserValue.isTranslationMode);
        }

        if (!translationId) {
            const translationResponse = await this.translationLoaderService.getSystemDefaultTranslationIdAsync();

            translationId = translationResponse.translationId;
            culture = translationResponse.culture;            
        }

        this.tcmCultureSystemService.setCulture(culture);

        await this.translateService.use(translationId.toString()).toPromise();

        if (this.authService.currentUserValue && this.authService.currentUserValue.isTranslationMode) {
            this.setTranslationMode(translationId.toString());
        }

        this.isInitialized = true;

        this.userSubscription = this.authService.currentUser$.subscribe(user => {
            if (user && user.translationId) {
                this.localStorageService.store(this.TranslationIdKey, user.translationId);

                this.tcmCultureSystemService.setCulture(user.culture);

                if (user.translationId.toString() != this.translateService.currentLang) {
                    this.translateService.use(user.translationId.toString());
                }

                if (this.getTranslationModeValue()) {
                    this.setTranslationMode(translationId.toString());
                }
            }
        });
    }

    setTranslation(translationId: string, translations: Object) {
        this.translateService.setTranslation(translationId, translations)
    }

    async resetTranslationMode(translationId: string) {
        const translation = await this.translationLoaderService.getTranslation(translationId).toPromise();
        this.setTranslation(translationId, translation);
        this.translationMode$.next(false);
    }

    getEnumeration() {
        return this.translateService.translations[this.translateService.currentLang]["Enumerations"];
    }

    setTranslationMode(translationId: string) {
        const emptyTranslation =  {
            Enumerations:  this.getEnumeration()
          };

        this.setTranslation(translationId, emptyTranslation);
        this.translationMode$.next(true);
    }
    
    setTranslationModeValue(isTranslationMode: boolean) {
        this.localStorageService.store(this.translationModeKey, isTranslationMode);
    }

    getTranslationModeValue() : boolean {
       return this.localStorageService.retrieve(this.translationModeKey) || false;
    }
}
